import {
  fuelLogsForCar, deriveLedger, fuelLogsToCsv, spendingsToCsv, tripsToCsv,
  type CanonicalStore, type Car,
} from "../../core/index.ts";
import { BackRow } from "../forms/Modal.tsx";
import { longDate, round0 } from "../format.ts";

function stamp(): string {
  const d = new Date();
  const M = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${M[d.getMonth()]}-${String(d.getDate()).padStart(2, "0")}-${d.getFullYear()}`;
}
function download(text: string, filename: string, mime = "text/csv") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportView({ store, car, isPro, requirePro, onBack }: {
  store: CanonicalStore; car: Car; isPro: boolean; requirePro: () => void; onBack: () => void;
}) {
  const metric = store.profile.unitSystem === "metric";
  const currency = store.profile.displayCurrency;
  const plate = car.licensePlate ?? car.id.slice(0, 8);
  const gate = (fn: () => void) => (isPro ? fn() : requirePro());

  const fuel = () => fuelLogsForCar(store.fuelLogs, car.id);
  const spend = () => store.spendings.filter((s) => s.carId === car.id);
  const trips = () => store.trips.filter((t) => t.carId === car.id);

  function printReport() {
    const logs = [...fuel()].sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
    const ledger = deriveLedger(store, car.id);
    const w = window.open("", "_blank");
    if (!w) return;
    const esc = (s: string) => s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]!));
    const rows = logs.map((l) => `<tr><td>${l.date ? longDate(l.date) : "—"}</td><td>${round0(l.odometer)}</td><td>${l.volumeAdded.toFixed(1)}</td><td>${round0(l.totalCost)} ${currency}</td><td>${esc(l.stationName ?? "—")}</td></tr>`).join("");
    const cats = ledger.byCategory.map((c) => `<tr><td>${esc(c.category)}</td><td>${c.count}</td><td>${round0(c.total)} ${currency}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(car.make ?? "")} ${esc(car.model ?? "")} — Report</title>
      <style>body{font-family:system-ui,sans-serif;color:#0a0b0d;margin:40px;font-size:12px}h1{font-size:24px;margin:0}h2{font-size:13px;letter-spacing:2px;color:#cc8800;margin:24px 0 8px;text-transform:uppercase}
      table{width:100%;border-collapse:collapse;margin-top:6px}th,td{text-align:left;padding:5px 8px;border-bottom:1px solid #eee;font-size:11px}th{background:#2a2d33;color:#fff}
      .meta{color:#666;font-size:11px;margin-top:4px}.total{font-size:20px;font-weight:800}</style></head><body onload="window.print()">
      <h1>VEHICLE REPORT</h1><div class="meta">${esc((car.make ?? "").toUpperCase())} ${esc(car.model ?? "")} · ${car.year ?? ""} · PLATE ${esc(car.licensePlate ?? "—")}</div>
      <div class="meta">Generated ${new Date().toLocaleString()}</div>
      <h2>Total spend</h2><div class="total">${round0(ledger.total)} ${currency}</div>
      <h2>Spend by category</h2><table><tr><th>Category</th><th>Entries</th><th>Total</th></tr>${cats}</table>
      <h2>Fuel log (${logs.length})</h2><table><tr><th>Date</th><th>Odometer</th><th>Volume</th><th>Cost</th><th>Station</th></tr>${rows}</table>
      </body></html>`);
    w.document.close();
  }

  const Row = ({ title, sub, onClick }: { title: string; sub: string; onClick: () => void }) => (
    <button className="panel" style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", cursor: "pointer" }} onClick={onClick}>
      <span className="amber" style={{ fontSize: 16 }}>⬇</span>
      <div style={{ flex: 1 }}>
        <div className="mono cream" style={{ fontSize: 12, fontWeight: 600 }}>{title}{isPro ? "" : " (PRO)"}</div>
        <div className="mono muted" style={{ fontSize: 10, marginTop: 2 }}>{sub}</div>
      </div>
      <span className="mono muted" style={{ fontSize: 14 }}>›</span>
    </button>
  );

  return (
    <div className="container glow">
      <BackRow onClick={onBack} />
      <div className="eyebrow amber" style={{ marginTop: 4 }}>EXPORT · <span className="muted">{car.model}</span></div>
      <div className="headline">Take your <span className="accent">data.</span></div>

      <div className="divider" style={{ marginTop: 14 }}><span className="label">CSV FILES</span><span className="line" /></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Row title="FUEL & CHARGE LOG" sub="Every fill-up / charge as CSV" onClick={() => gate(() => download(fuelLogsToCsv(fuel(), metric), `${stamp()}-fuel-${plate}.csv`))} />
        <Row title="EXPENSES" sub="All spendings as CSV" onClick={() => gate(() => download(spendingsToCsv(spend()), `${stamp()}-expenses-${plate}.csv`))} />
        <Row title="TRIPS" sub="Trip logbook as CSV" onClick={() => gate(() => download(tripsToCsv(trips(), metric), `${stamp()}-trips-${plate}.csv`))} />
      </div>

      <div className="divider" style={{ marginTop: 18 }}><span className="label">PDF REPORT</span><span className="line" /></div>
      <Row title="VEHICLE REPORT" sub="Summary, categories & fuel log — opens the print dialog (save as PDF)" onClick={() => gate(printReport)} />
      <div style={{ height: 20 }} />
    </div>
  );
}
